/**
 * Web chat tool-card for `artifactList`: the user's recent files as rows.
 * A document, deck or page opens in the docked canvas panel; an image opens
 * the lightbox. `artifact-list-card.native.tsx` is the twin.
 */
import React, { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { ArtifactViewer } from '../../artifacts/ArtifactViewer';
import { openArtifactPanel } from '../../artifacts/artifact-panel-store';
import {
  parseArtifactListResult,
  type ArtifactCardModel,
} from '../../artifacts/artifact-model';
import { CardShell } from './card-shell';
import { CardChevron, CardEmpty, CardHeader, CardRow } from './card-ui';
import type { ToolCardProps } from './types';

const KIND_ICONS: Record<ArtifactCardModel['kind'], string> = {
  document: 'description',
  slides: 'slideshow',
  page: 'web',
  image: 'image',
  file: 'draft',
};

export const ArtifactListCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('common');
  const { artifacts, hasMore } = useMemo(() => parseArtifactListResult(result), [result]);
  const [openImageId, setOpenImageId] = useState<string | null>(null);

  if (!artifacts.length) {
    return (
      <CardShell>
        <CardEmpty>{t('chat.artifacts.list.empty')}</CardEmpty>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader
        title={t('chat.artifacts.list.title')}
        leading={
          <MaterialSymbol name="folder" size={16} style={{ color: 'var(--text-tertiary)' }} />
        }
      />

      {artifacts.map((artifact) => {
        const kindLabel = t(`chat.artifacts.kind.${artifact.kind}`);
        return (
          <CardRow
            key={artifact.artifactId}
            divider
            title={artifact.title || kindLabel}
            subtitle={kindLabel}
            meta={
              artifact.status === 'pending'
                ? t('chat.artifacts.preparing')
                : artifact.status === 'failed'
                  ? t('chat.tasks.failed')
                  : undefined
            }
            leading={
              <span
                aria-hidden
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.06)',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialSymbol name={KIND_ICONS[artifact.kind]} size={18} />
              </span>
            }
            accessibilityHint={t('chat.artifacts.actions.open')}
            onPress={() =>
              artifact.kind === 'image'
                ? setOpenImageId(artifact.artifactId)
                : openArtifactPanel(artifact.artifactId)
            }
            trailing={<CardChevron />}
          />
        );
      })}

      {hasMore ? <CardEmpty>{t('chat.artifacts.list.more')}</CardEmpty> : null}

      {openImageId ? (
        <ArtifactViewer artifactId={openImageId} kindHint="image" onClose={() => setOpenImageId(null)} />
      ) : null}
    </CardShell>
  );
};
